import { useEffect, useRef, useState } from 'react';
import { Cart } from 'shared/types/cart';
import { WithSDKError } from 'shared/types/shared';
import { sdk } from 'sdk';
import { getAccount, getIsAuthenticated, useAppSelector } from 'store';
import { makeSDKCallWithRetries } from 'utils';
import { withCookieValidation } from 'utils/edx-auth';
import { conversionEvents } from 'utils/eventing';
import { TagularAction } from 'utils/eventing/types';
import newrelic from 'utils/monitoring/newrelicWrapper';
import { getVariantCategoryFromUrl, hyphenateForTagular } from 'utils/tagular';

type AutoEnrollResult = {
  success: boolean;
  alreadyEnrolled: boolean;
  enrolledCourseIds: string[];
  errors: string[];
};

type AutoEnrollResponse = WithSDKError<AutoEnrollResult>;

type UseAutoEnrollmentProps = {
  cart: Partial<Cart> | undefined;
  isCartLoading: boolean;
  isStraightToCheckout: boolean;
  courseRunKey?: string;
};

/**
 * Triggers audit enrollment on checkout page load for Straight-to-Checkout users.
 * Idempotent: skips if already attempted for the current cart, and the backend
 * checks existing enrollments before creating new ones.
 */
export const useAutoEnrollment = ({
  cart,
  isCartLoading,
  isStraightToCheckout,
  courseRunKey,
}: UseAutoEnrollmentProps) => {
  const account = useAppSelector(getAccount);
  const customerId = account?.accountId;

  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const attemptedCartId = useRef<string | null>(null);

  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentResult, setEnrollmentResult] =
    useState<AutoEnrollResult | null>(null);

  const hasBundleItems =
    cart?.lineItems?.some((item) => item.bundleId) ?? false;

  const shouldSkipEnrollment =
    !isStraightToCheckout ||
    !isAuthenticated ||
    isCartLoading ||
    !courseRunKey ||
    !cart?.cartId ||
    !cart.lineItems?.length ||
    hasBundleItems ||
    attemptedCartId.current === cart.cartId;

  useEffect(() => {
    if (shouldSkipEnrollment) {
      return;
    }

    attemptedCartId.current = cart?.cartId ?? null;
    setIsEnrolling(true);

    const enroll = async () => {
      try {
        const lineItem = cart?.lineItems?.[0];
        const attrs = lineItem?.variant?.attributes;

        const response = await withCookieValidation(() =>
          makeSDKCallWithRetries(
            'enrollment/autoEnrollForCheckoutSingleCourse',
            customerId ?? '',
            () =>
              sdk.callAction<AutoEnrollResponse>({
                actionName: 'enrollment/autoEnrollForCheckoutSingleCourse',
                payload: {
                  courseRunKey,
                },
              }),
          ),
        );

        if (response.isError) {
          console.error(
            '[useAutoEnrollment] Enrollment action failed',
            response.error,
          );
          return;
        }

        if (response.data.isError) {
          console.error(
            '[useAutoEnrollment] Enrollment action failed',
            response.data.error,
          );
          return;
        }

        setEnrollmentResult(response.data);

        if (response.data.success || response.data.alreadyEnrolled) {
          if (lineItem) {
            const courseKey =
              attrs?.['course-key'] ?? lineItem.productKey ?? '';

            const marketingUrl = attrs?.['url-course'] ?? '';
            const fromUrl = marketingUrl
              ? getVariantCategoryFromUrl(marketingUrl)
              : { variant: '', category: '' };

            const product = {
              brand: hyphenateForTagular(attrs?.['brand-text'] ?? ''),
              category:
                fromUrl.category ||
                hyphenateForTagular(attrs?.['primary-subject-area'] ?? ''),
              variant: fromUrl.variant || 'courses',
              name: lineItem.name ?? '',
              id: courseKey,
              externalId: courseRunKey,
            };

            const fireConversionEvent = (action: TagularAction) => {
              const { conversionTracked } = conversionEvents({
                metadata: {
                  customName: 'enroll',
                  action,
                  category: 'Enrollment',
                  timestamp: new Date().toISOString(),
                  productList: [product],
                },
              });
              conversionTracked();
            };

            // Mirror the CYT funnel (Started on CTA → Submitted on next page):
            //  - alreadyEnrolled: edx.org already enrolled the user and fired
            //    Started before redirect (logged-in STC). Only fire Submitted.
            //  - new enrollment: this hook created the enrollment itself
            //    (logged-out STC, where enrollment moved from CYT to checkout).
            //    Fire Started then Submitted so the funnel stays complete.
            if (!response.data.alreadyEnrolled) {
              fireConversionEvent(TagularAction.STARTED);
            }
            fireConversionEvent(TagularAction.SUBMITTED);

            const anonymousId =
              window.tagular?.('getAliasSet')?.anonymousId ?? '';
            newrelic.addPageAction('CourseEnroll.silentEnrollmentCreated', {
              courseId: courseRunKey,
              anonymousId,
            });
          }
        }
      } catch (error) {
        console.error('[useAutoEnrollment] Unexpected error', error);
      } finally {
        setIsEnrolling(false);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    enroll();
  }, [shouldSkipEnrollment, cart?.cartId, cart?.lineItems, customerId]);

  return { isEnrolling, enrollmentResult };
};
